import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, CircularProgress, Alert, Pagination,
  InputBase, IconButton, Tooltip, useTheme, useMediaQuery,
} from '@mui/material';
import {
  ArrowBack, Refresh, Payments, ChevronRightRounded,
  ShoppingCart, Replay, TuneOutlined, CurrencyExchangeOutlined,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { getAccountTransactions, getAllAccounts } from 'api/wallet';
import WalletTopupDialog from 'components/WalletTopupDialog';
import WalletPurchaseOrderDialog from 'components/WalletPurchaseOrderDialog';
import WalletRefundDialog from 'components/WalletRefundDialog';
import AdjustBalanceDialog from 'components/AdjustBalanceDialog';
import ExchangeCurrencyDialog from 'components/ExchangeCurrencyDialog';
import WalletTransactionAdminDetailDialog from 'components/WalletTransactionAdminDetailDialog';
import { useTranslation } from 'react-i18next';
import { user as getUser } from 'localStorage';
import { hasPageAccess } from 'utils/pagePermissions';
import { useUserRefreshTick } from 'hooks/useUserRefreshTick';
import { formatWalletTxnDetail } from 'utils/formatWalletTxnDetail';

// Bento-family redesign — see scenes/wallet/accounts.jsx for the shared local
// token constants; this page mirrors scenes/financial/transactions.jsx's wallet
// (non-branch) variant, kept as 3 separate currencies since accounts here always
// carry THB/LAK/USD balances together (no single active currency to net against).
const FONT = "'Plus Jakarta Sans', 'Noto Sans Lao', system-ui, sans-serif";
const MONO = "'Space Grotesk', 'Noto Sans Lao', ui-monospace, monospace";
const CURRENCIES = ['THB', 'LAK', 'USD'];
const CUR_SYM = { THB: '฿', LAK: '₭', USD: '$' };
const PURPOSES = ['deposit', 'withdraw', 'transfer', 'reversal', 'refund', 'payment', 'exchange', 'adjustment'];
const money = (n, c) => `${CUR_SYM[c] || ''}${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtD = (d) => (d ? new Date(d).toLocaleDateString() : '');
const fmtT = (d) => (d ? new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');
const initials = (s) => (s || '?').slice(0, 2).toUpperCase();
const ARROW_OUT = 'M6 10V2M2 6l4-4 4 4';
const ARROW_IN = 'M6 2v8M2 6l4 4 4-4';

function typeStyle(purpose, txnType) {
  if (txnType === 'debit') return { bg: '#fff1f2', border: '#fca5a5', col: '#c0343a', arrow: ARROW_OUT };
  if (purpose === 'exchange') return { bg: '#f0f9ff', border: '#7dd3fc', col: '#0369a1', arrow: ARROW_IN };
  return { bg: '#f0fdf4', border: '#86efac', col: '#15803d', arrow: ARROW_IN };
}

const labelSx = { fontSize: '10.5px', letterSpacing: '.14em', color: '#a8a3b4', fontWeight: 700 };
const headCell = { fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: '#a8a3b4', textTransform: 'uppercase' };
const GRID = '1.5fr 1.3fr 1.2fr 1.3fr 2fr 36px';

function Kpi({ label, value, color, highlight, negative, onPayDebt, payDebtLabel }) {
  return (
    <Box sx={{
      background: highlight ? 'linear-gradient(155deg,#fff 0%,#f5efff 100%)' : '#fff',
      border: `1px solid ${highlight ? '#ece4fb' : '#ece9f1'}`,
      borderRadius: '18px', p: '18px 20px',
      boxShadow: highlight
        ? '0 1px 2px rgba(20,18,30,.04),0 14px 28px -20px rgba(86,40,160,.22)'
        : '0 1px 2px rgba(20,18,30,.04),0 14px 28px -20px rgba(20,18,30,.12)',
    }}>
      <Box sx={labelSx}>{label.toUpperCase()}</Box>
      <Box sx={{ fontFamily: MONO, fontSize: 22, fontWeight: 700, color: color || '#161420', mt: '8px' }}>{value}</Box>
      {negative && (
        <Box component="button" onClick={onPayDebt} sx={{
          display: 'inline-flex', alignItems: 'center', fontFamily: FONT, fontSize: 11, fontWeight: 700,
          color: '#c0343a', background: '#fff1f2', border: 'none', borderRadius: '999px',
          padding: '4px 10px', mt: '10px', cursor: 'pointer', '&:hover': { background: '#ffe4e6' },
        }}>
          {payDebtLabel}
        </Box>
      )}
    </Box>
  );
}

function PillButton({ children, ...props }) {
  return (
    <Box component="button" {...props}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: FONT, fontSize: 13, fontWeight: 700, color: '#5b21b6', background: '#fff', border: '1.5px solid #ddd0fa', borderRadius: '11px', padding: '10px 18px', cursor: 'pointer', boxShadow: '0 1px 2px rgba(20,18,30,.04)', '&:hover': { background: '#f3f0fc' }, ...props.sx }}>
      {children}
    </Box>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
const AccountTransactions = () => {
  const { t } = useTranslation();
  const { accountId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  useUserRefreshTick(); // re-render when the cached profile refreshes — see hooks/useUserRefreshTick.js
  const _user = getUser();
  const can = (slug) => hasPageAccess(_user, slug);

  const [account, setAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(true);

  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTxId, setSelectedTxId] = useState(null);
  const openDetail = (tx) => { setSelectedTxId(tx.id); setDetailOpen(true); };

  const [topupOpen, setTopupOpen] = useState(false);
  const [topupPrefill, setTopupPrefill] = useState(null);
  const payDebt = (currency, amount) => {
    setTopupPrefill({ amount: String(Math.round(Math.abs(Number(amount) || 0) * 100) / 100), currency });
    setTopupOpen(true);
  };
  const [purchaseOrderOpen, setPurchaseOrderOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [exchangeOpen, setExchangeOpen] = useState(false);

  const [currencyFilter, setCurrencyFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [purposeFilter, setPurposeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const fetchAccount = useCallback(async () => {
    try {
      setAccountLoading(true);
      const result = await getAllAccounts(1, 1, accountId, 'all');
      const found = result.data.accounts.find((a) => a.id === accountId);
      setAccount(found || null);
    } catch {
      // non-critical
    } finally {
      setAccountLoading(false);
    }
  }, [accountId]);

  useEffect(() => { fetchAccount(); }, [fetchAccount]);

  const loadTxns = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const result = await getAccountTransactions(
        accountId, page, 20,
        currencyFilter || undefined,
        typeFilter || undefined,
        purposeFilter || undefined,
        dateFrom || undefined,
        dateTo || undefined,
        search || undefined,
      );
      setTxns(result.data.transactions);
      setPagination(result.data.pagination);
    } catch (err) {
      setError(err.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [accountId, currencyFilter, typeFilter, purposeFilter, dateFrom, dateTo, search]);

  useEffect(() => { setCurrentPage(1); loadTxns(1); }, [currencyFilter, typeFilter, purposeFilter, dateFrom, dateTo, search]);
  useEffect(() => { loadTxns(currentPage); }, [currentPage]);

  const clearFilters = () => {
    setCurrencyFilter(''); setTypeFilter(''); setPurposeFilter('');
    setDateFrom(''); setDateTo(''); setSearchInput(''); setSearch('');
  };
  const hasFilters = currencyFilter || typeFilter || purposeFilter || dateFrom || dateTo || search;

  const refreshAll = () => { loadTxns(currentPage); };

  // After a top-up/purchase/refund/adjust: jump to page 1 (newest-first sort, so
  // the just-created transaction is actually visible) and pull the fresh balance.
  const refreshAfterTxn = () => {
    fetchAccount();
    setCurrentPage(1);
    loadTxns(1);
  };

  return (
    <Box sx={{ fontFamily: FONT, maxWidth: 1280, mr: 'auto', p: { xs: '20px', md: '28px 40px 60px' } }}>
      {/* header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <Box sx={{ width: 48, height: 48, borderRadius: '14px', background: 'linear-gradient(135deg,#7c3aed,#4c1d95)', color: '#fff', fontFamily: MONO, fontWeight: 700, fontSize: 17, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
            {initials(account?.customer?.name)}
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <Box component="h1" sx={{ m: 0, fontSize: 22, fontWeight: 800, color: '#16131f', letterSpacing: '-.01em' }}>{account?.customer?.name || '—'}</Box>
              {account?.customer?.whatsapp && <Box component="span" sx={{ fontSize: 14, fontWeight: 500, color: '#9b96a6', fontFamily: MONO }}>· {account.customer.whatsapp}</Box>}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '5px', flexWrap: 'wrap' }}>
              <Box sx={{ fontSize: '11.5px', fontWeight: 700, color: '#6d28d9', background: '#f3f0fc', border: '1px solid #ddd0fa', padding: '3px 10px', borderRadius: '999px' }}>
                {account?.id || accountId}
              </Box>
              {account && (
                <Box sx={{ fontSize: '11.5px', fontWeight: 700, color: account.validflag ? '#15803d' : '#6b6775', background: account.validflag ? '#eafaf0' : '#f4f2f8', padding: '3px 10px', borderRadius: '999px' }}>
                  {t(account.validflag ? 'wallet.admin.active' : 'wallet.admin.inactive')}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {can('wallet/accounts/adjust') && (isMobile ? (
            <Tooltip title={t('wallet.admin.adjustBalance')}>
              <span><IconButton onClick={() => setAdjustOpen(true)} disabled={!account} sx={{ color: '#6d28d9', border: '1.5px solid #ddd0fa', borderRadius: '11px' }}><TuneOutlined fontSize="small" /></IconButton></span>
            </Tooltip>
          ) : (
            <PillButton onClick={() => setAdjustOpen(true)} disabled={!account} sx={{ opacity: account ? 1 : 0.5, cursor: account ? 'pointer' : 'not-allowed' }}>
              <TuneOutlined sx={{ fontSize: 16, color: '#6d28d9' }} />{t('wallet.admin.adjustBalance')}
            </PillButton>
          ))}
          {can('wallet/accounts/adjust') && (isMobile ? (
            <Tooltip title={t('wallet.admin.exchangeCurrency')}>
              <span><IconButton onClick={() => setExchangeOpen(true)} disabled={!account} sx={{ color: '#6d28d9', border: '1.5px solid #ddd0fa', borderRadius: '11px' }}><CurrencyExchangeOutlined fontSize="small" /></IconButton></span>
            </Tooltip>
          ) : (
            <PillButton onClick={() => setExchangeOpen(true)} disabled={!account} sx={{ opacity: account ? 1 : 0.5, cursor: account ? 'pointer' : 'not-allowed' }}>
              <CurrencyExchangeOutlined sx={{ fontSize: 16, color: '#6d28d9' }} />{t('wallet.admin.exchangeCurrency')}
            </PillButton>
          ))}
          {can('wallet/accounts/topup') && (isMobile ? (
            <Tooltip title={t('wallet.admin.topUp')}>
              <span><IconButton onClick={() => setTopupOpen(true)} disabled={!account} sx={{ color: '#fff', background: '#6d28d9', '&:hover': { background: '#5b21b6' } }}><Payments fontSize="small" /></IconButton></span>
            </Tooltip>
          ) : (
            <Box component="button" onClick={() => setTopupOpen(true)} disabled={!account}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: FONT, fontSize: 13, fontWeight: 700, color: '#fff', background: '#6d28d9', border: 'none', borderRadius: '11px', padding: '10px 18px', cursor: account ? 'pointer' : 'not-allowed', opacity: account ? 1 : 0.5, boxShadow: '0 1px 2px rgba(20,18,30,.04),0 8px 20px -10px rgba(109,40,217,.5)', '&:hover': { background: '#5b21b6' } }}>
              <Payments sx={{ fontSize: 16 }} />{t('wallet.admin.topUp')}
            </Box>
          ))}
          {can('wallet/accounts/purchase-order') && (isMobile ? (
            <Tooltip title={t('wallet.admin.purchaseOrderTab')}>
              <span><IconButton onClick={() => setPurchaseOrderOpen(true)} disabled={!account} sx={{ color: '#6d28d9', border: '1.5px solid #ddd0fa', borderRadius: '11px' }}><ShoppingCart fontSize="small" /></IconButton></span>
            </Tooltip>
          ) : (
            <PillButton onClick={() => setPurchaseOrderOpen(true)} disabled={!account} sx={{ opacity: account ? 1 : 0.5, cursor: account ? 'pointer' : 'not-allowed' }}>
              <ShoppingCart sx={{ fontSize: 16, color: '#6d28d9' }} />{t('wallet.admin.purchaseOrderTab')}
            </PillButton>
          ))}
          {can('wallet/accounts/refund') && (isMobile ? (
            <Tooltip title={t('wallet.admin.refundTab')}>
              <span><IconButton onClick={() => setRefundOpen(true)} disabled={!account} sx={{ color: '#6d28d9', border: '1.5px solid #ddd0fa', borderRadius: '11px' }}><Replay fontSize="small" /></IconButton></span>
            </Tooltip>
          ) : (
            <PillButton onClick={() => setRefundOpen(true)} disabled={!account} sx={{ opacity: account ? 1 : 0.5, cursor: account ? 'pointer' : 'not-allowed' }}>
              <Replay sx={{ fontSize: 16, color: '#6d28d9' }} />{t('wallet.admin.refundTab')}
            </PillButton>
          ))}
          {isMobile ? (
            <Tooltip title={t('common.refresh')}><span><IconButton onClick={refreshAll} disabled={loading} sx={{ color: '#6d28d9', border: '1.5px solid #ddd0fa', borderRadius: '11px' }}><Refresh /></IconButton></span></Tooltip>
          ) : (
            <PillButton onClick={refreshAll} disabled={loading}><Refresh sx={{ fontSize: 16, color: '#6d28d9' }} />{t('common.refresh')}</PillButton>
          )}
          {isMobile ? (
            <Tooltip title={t('wallet.admin.backToAccounts')}><span><IconButton onClick={() => navigate('/wallet/accounts')} sx={{ color: '#6d28d9', border: '1.5px solid #ddd0fa', borderRadius: '11px' }}><ArrowBack /></IconButton></span></Tooltip>
          ) : (
            <PillButton onClick={() => navigate('/wallet/accounts')}><ArrowBack sx={{ fontSize: 16, color: '#6d28d9' }} />{t('wallet.admin.backToAccounts')}</PillButton>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* KPI strip — per-currency balances */}
      {!accountLoading && account && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '16px', mt: '22px' }}>
          <Kpi highlight label="THB" value={money(account.THB, 'THB')} color={account.THB < 0 ? '#c0343a' : '#6d28d9'}
            negative={account.THB < 0} payDebtLabel={t('wallet.admin.payDebt')} onPayDebt={() => payDebt('THB', account.THB)} />
          <Kpi label="LAK" value={money(account.LAK, 'LAK')} color={account.LAK < 0 ? '#c0343a' : undefined}
            negative={account.LAK < 0} payDebtLabel={t('wallet.admin.payDebt')} onPayDebt={() => payDebt('LAK', account.LAK)} />
          <Kpi label="USD" value={money(account.USD, 'USD')} color={account.USD < 0 ? '#c0343a' : undefined}
            negative={account.USD < 0} payDebtLabel={t('wallet.admin.payDebt')} onPayDebt={() => payDebt('USD', account.USD)} />
        </Box>
      )}

      {/* filter bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mt: '22px', flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 200, maxWidth: 280 }}>
          <InputBase fullWidth placeholder={t('wallet.admin.pressEnterToSearch')} value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)}
            sx={{ fontFamily: FONT, fontSize: 13, px: '14px', py: '9px', borderRadius: '11px', border: '1px solid #ece9f1', background: '#fff', boxShadow: '0 1px 2px rgba(20,18,30,.04)' }} />
        </Box>
        <DateBox label={t('wallet.admin.fromDate')} value={dateFrom} onChange={(v) => setDateFrom(v)} />
        <DateBox label={t('wallet.admin.toDate')} value={dateTo} onChange={(v) => setDateTo(v)} />
        <Box sx={{ display: 'flex', gap: '4px', background: '#fff', border: '1px solid #ece9f1', p: '4px', borderRadius: '11px', boxShadow: '0 1px 2px rgba(20,18,30,.04)' }}>
          {['', ...CURRENCIES].map((c) => {
            const active = currencyFilter === c;
            return (
              <Box component="button" key={c || 'all'} onClick={() => setCurrencyFilter(c)}
                sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, padding: '6px 13px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: active ? '#6d28d9' : 'transparent', color: active ? '#fff' : '#7c7787' }}>
                {c || t('wallet.admin.all')}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ display: 'flex', gap: '4px', background: '#fff', border: '1px solid #ece9f1', p: '4px', borderRadius: '11px', boxShadow: '0 1px 2px rgba(20,18,30,.04)' }}>
          {[{ v: '', l: t('wallet.admin.all') }, { v: 'credit', l: t('wallet.admin.credit') }, { v: 'debit', l: t('wallet.admin.debit') }].map((opt) => {
            const active = typeFilter === opt.v;
            return (
              <Box component="button" key={opt.v || 'all'} onClick={() => setTypeFilter(opt.v)}
                sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, padding: '6px 13px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: active ? '#6d28d9' : 'transparent', color: active ? '#fff' : '#7c7787' }}>
                {opt.l}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ display: 'flex', gap: '4px', background: '#fff', border: '1px solid #ece9f1', p: '4px', borderRadius: '11px', boxShadow: '0 1px 2px rgba(20,18,30,.04)', flexWrap: 'wrap' }}>
          {['', ...PURPOSES].map((p) => {
            const active = purposeFilter === p;
            return (
              <Box component="button" key={p || 'all'} onClick={() => setPurposeFilter(p)}
                sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, padding: '6px 13px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: active ? '#6d28d9' : 'transparent', color: active ? '#fff' : '#7c7787', textTransform: 'capitalize' }}>
                {p || t('wallet.admin.all')}
              </Box>
            );
          })}
        </Box>
        {hasFilters && (
          <Box component="button" onClick={clearFilters}
            sx={{ fontFamily: FONT, fontSize: '12.5px', fontWeight: 700, color: '#7c7787', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
            {t('wallet.admin.clearFilters')}
          </Box>
        )}
        <Box sx={{ ml: 'auto', fontSize: '12.5px', color: '#a8a3b4', fontWeight: 600 }}>
          {t('wallet.admin.transactionsFound', { count: pagination.total || 0 })}{hasFilters ? ` ${t('wallet.admin.filtered')}` : ''}
        </Box>
      </Box>

      {/* list */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={6}><CircularProgress color="secondary" /></Box>
      ) : txns.length === 0 ? (
        <Box sx={{ background: '#fff', border: '1px solid #ece9f1', borderRadius: '22px', mt: '16px', p: '50px 24px', textAlign: 'center', color: '#b9b4c4' }}>
          {t('wallet.admin.noTransactions')}
        </Box>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', mt: '16px' }}>
          {txns.map((tx) => {
            const s = typeStyle(tx.purpose, tx.txn_type);
            const out = tx.txn_type === 'debit';
            return (
              <Box key={tx._id} onClick={() => openDetail(tx)}
                sx={{ background: '#fff', border: '1px solid #ece9f1', borderRadius: '16px', p: '14px 16px', cursor: 'pointer', boxShadow: '0 1px 2px rgba(20,18,30,.04)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: 12, fontWeight: 700, padding: '5px 11px', borderRadius: '999px', border: `1.5px solid ${s.border}`, background: s.bg, color: s.col, textTransform: 'capitalize' }}>
                    <Box component="svg" width="11" height="11" viewBox="0 0 12 12" fill="none"><path d={s.arrow} stroke={s.col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></Box>
                    {tx.purpose}
                  </Box>
                  <Box sx={{ fontFamily: MONO, fontSize: 17, fontWeight: 700, color: out ? '#c0343a' : '#117a4f' }}>{out ? '-' : '+'}{money(tx.amount, tx.currency)}</Box>
                </Box>
                <Box sx={{ fontSize: 12, color: '#9b96a6', mt: '8px' }}>{fmtD(tx.createdAt)} · {fmtT(tx.createdAt)}</Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', mt: '6px' }}>
                  <Box sx={{ fontSize: 12, color: '#6b6775' }}>{t('wallet.admin.balanceAfter')}: <Box component="span" sx={{ fontFamily: MONO, fontWeight: 600, color: '#2c2937' }}>{money(tx.balanceAfter, tx.currency)}</Box></Box>
                  <Box sx={{ fontFamily: MONO, fontSize: '11px', color: '#b3aebd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '45%' }}>{formatWalletTxnDetail(tx, t) || tx.reference || ''}</Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{ background: '#fff', border: '1px solid #ece9f1', borderRadius: '22px', overflow: 'hidden', mt: '16px', boxShadow: '0 1px 2px rgba(20,18,30,.04),0 16px 40px -28px rgba(20,18,30,.18)' }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 760 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', p: '13px 24px', background: '#faf9fc', borderBottom: '1.5px solid #ece9f1' }}>
                <Box sx={headCell}>{t('wallet.admin.date')}</Box>
                <Box sx={headCell}>{t('wallet.admin.purpose')}</Box>
                <Box sx={{ ...headCell, color: '#6d28d9', textAlign: 'right' }}>Amount</Box>
                <Box sx={{ ...headCell, textAlign: 'right' }}>{t('wallet.admin.balanceAfter')}</Box>
                <Box sx={{ ...headCell, pl: '24px' }}>{t('wallet.admin.remark')}</Box>
                <Box />
              </Box>
              {txns.map((tx) => {
                const s = typeStyle(tx.purpose, tx.txn_type);
                const out = tx.txn_type === 'debit';
                return (
                  <Box key={tx._id} onClick={() => openDetail(tx)}
                    sx={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', p: '0 24px', borderTop: '1px solid #f4f2f8', minHeight: 60, cursor: 'pointer', '&:hover': { background: '#faf9fc' } }}>
                    <Box sx={{ py: '14px' }}>
                      <Box sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 500, color: '#2c2937' }}>{fmtD(tx.createdAt)}</Box>
                      <Box sx={{ fontSize: 11, color: '#b3aebd', mt: '2px' }}>{fmtT(tx.createdAt)} · {tx.actionBy?.name}</Box>
                    </Box>
                    <Box>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: 12, fontWeight: 700, padding: '5px 11px', borderRadius: '999px', border: `1.5px solid ${s.border}`, background: s.bg, color: s.col, textTransform: 'capitalize' }}>
                        <Box component="svg" width="11" height="11" viewBox="0 0 12 12" fill="none"><path d={s.arrow} stroke={s.col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></Box>
                        {tx.purpose}
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right', fontFamily: MONO, fontSize: 15, fontWeight: 700, color: out ? '#c0343a' : '#117a4f' }}>{out ? '-' : '+'}{money(tx.amount, tx.currency)}</Box>
                    <Box sx={{ textAlign: 'right', fontFamily: MONO, fontSize: 14, fontWeight: 600, color: '#2c2937' }}>{money(tx.balanceAfter, tx.currency)}</Box>
                    <Box sx={{ minWidth: 0, pl: '24px', pr: '12px' }}>
                      <Box sx={{ fontFamily: MONO, fontSize: '11.5px', color: '#b3aebd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatWalletTxnDetail(tx, t) || tx.reference || '—'}</Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ChevronRightRounded sx={{ fontSize: 20, color: '#b9b4c4' }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}

      {pagination.pages > 1 && (
        <Box display="flex" justifyContent="center" mt="20px">
          <Pagination count={pagination.pages} page={currentPage} onChange={(_, p) => setCurrentPage(p)} color="secondary" size={isMobile ? 'small' : 'medium'} />
        </Box>
      )}

      <WalletTransactionAdminDetailDialog open={detailOpen} onClose={() => setDetailOpen(false)} txId={selectedTxId} />

      <AdjustBalanceDialog
        mode="wallet"
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        balances={{ THB: account?.THB || 0, LAK: account?.LAK || 0, USD: account?.USD || 0 }}
        target={{ id: account?.id, name: account?.customer?.name, phone: account?.customer?.whatsapp }}
        onSuccess={(res) => {
          setAdjustOpen(false);
          setSuccess(t('wallet.admin.balanceAdjustedSuccess', {
            type: t(`wallet.admin.${res.data.adjustType === 'add' ? 'credited' : 'debited'}`),
            amount: Math.abs(res.data.balanceAfter - res.data.balanceBefore), currency: res.data.currency, txnId: res.data.transactionId,
          }));
          setAccount((a) => (a ? { ...a, ...res.data.newBalance } : a));
          refreshAfterTxn();
        }}
      />

      <ExchangeCurrencyDialog
        open={exchangeOpen}
        onClose={() => setExchangeOpen(false)}
        balances={{ THB: account?.THB || 0, LAK: account?.LAK || 0, USD: account?.USD || 0 }}
        blocked={{ THB: account?.blockedTHB || 0, LAK: account?.blockedLAK || 0, USD: account?.blockedUSD || 0 }}
        target={{ id: account?.id, name: account?.customer?.name, phone: account?.customer?.whatsapp }}
        onSuccess={(res) => {
          const d = res.data;
          setExchangeOpen(false);
          setSuccess(t('wallet.admin.exchangeSuccess', {
            from: money(d.givenAmount, d.fromCurrency) + ' ' + d.fromCurrency,
            to: money(d.receivedAmount, d.toCurrency) + ' ' + d.toCurrency,
            txnId: d.transactionIds.credit,
          }));
          setAccount((a) => (a ? { ...a, ...d.newBalance } : a));
          refreshAfterTxn();
        }}
      />

      <WalletTopupDialog
        open={topupOpen}
        onClose={() => { setTopupOpen(false); setTopupPrefill(null); }}
        account={account}
        defaultAmount={topupPrefill?.amount}
        defaultCurrency={topupPrefill?.currency}
        onSuccess={() => {
          setSuccess(t('wallet.admin.cashPaymentSuccess'));
          refreshAfterTxn();
        }}
      />

      <WalletPurchaseOrderDialog
        open={purchaseOrderOpen}
        onClose={() => setPurchaseOrderOpen(false)}
        account={account}
        onSuccess={({ amount, currency }) => {
          setSuccess(t('wallet.admin.purchaseSuccess', { amount: amount.toLocaleString(), currency }));
          refreshAfterTxn();
        }}
      />

      <WalletRefundDialog
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        account={account}
        onSuccess={({ amount, currency }) => {
          setSuccess(t('wallet.admin.refundSuccess', { amount: amount.toLocaleString(), currency }));
          refreshAfterTxn();
        }}
      />
    </Box>
  );
};

function DateBox({ label, value, onChange }) {
  return (
    <Box sx={{ background: '#fff', border: '1px solid #ece9f1', borderRadius: '11px', px: '14px', height: 42, display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 1px 2px rgba(20,18,30,.04)' }}>
      <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: '#a8a3b4', letterSpacing: '.08em', whiteSpace: 'nowrap' }}>{label.toUpperCase()}</Box>
      <Box component="input" type="date" value={value} onChange={(e) => onChange(e.target.value)}
        sx={{ fontFamily: MONO, fontSize: 13, color: '#2c2937', border: 'none', outline: 'none', background: 'transparent', cursor: 'pointer' }} />
    </Box>
  );
}

export default AccountTransactions;
